import { Flex, Icon, Link, Text, Tooltip, TooltipContent } from '@customerio/pluma-components/react';
import type { WorkspaceObjectType } from '@/features/workspaces';
import { generatePath } from '@/services/router/generate-path';
import { isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import { parseLogType } from '../activity-log-types';
import { activityLogDisplayName, type IdentifierConfig } from '../display-name';
import { useActivityLogDelivery } from '../hooks';
import type { ActivityLog, ActivityLogCustomer, ActivityLogRow } from '../types';

/** A log belongs in the Object variant of the Person/Object column. */
export function isObjectLog(log: ActivityLog): boolean {
  return !isEmptyString(log.object_id ?? '') || !isEmptyString(log.old_object_id ?? '');
}

/** Content-event types that link to their in-app broadcast. */
const CONTENT_EVENT_TYPES = ['viewed_content', 'clicked_content', 'converted_content'];

/** Segment overview path — shared by the segment-membership and segment-build name links. */
export function segmentPath(workspaceId: string, segmentId: string | number): string {
  return generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId', { workspaceId, segmentId });
}

const FIRST_NAME_KEYS = ['first_name', 'firstname', 'first name', 'first'];
const LAST_NAME_KEYS = ['last_name', 'lastname', 'last name', 'last'];
const FULL_NAME_KEYS = ['full_name', 'fullname', 'full name', 'name'];

function firstAttr(attrs: Record<string, string> | undefined, keys: string[]): string | null {
  if (attrs == null) return null;
  for (const key of keys) {
    const value = attrs[key];
    if (!isEmptyString(value ?? '')) return value;
  }
  return null;
}

/** Name from attributes: "first last" → a full-name attribute → null. */
function customerName(attributes: Record<string, string>): string | null {
  const first = firstAttr(attributes, FIRST_NAME_KEYS);
  const last = firstAttr(attributes, LAST_NAME_KEYS);
  if (first != null && last != null) return `${first} ${last}`;
  return firstAttr(attributes, FULL_NAME_KEYS);
}

/** Fallback identifier label when there's no name: email → phone → "{Person} with id {id}". */
function customerIdentifierDisplay(customer: ActivityLogCustomer, personSingular: string): string {
  const { email: identifierEmail, phone, id } = customer.identifiers ?? {};
  // Ember reads email from `attrs.email` (the customer attributes); fall back to the identifiers
  // email so an address that lives on either source still shows.
  const email = customer.attributes?.email ?? identifierEmail ?? '';
  if (!isEmptyString(email)) return email;
  if (!isEmptyString(phone ?? '')) return phone;
  return `${personSingular} with id ${id ?? customer.id}`;
}

/**
 * Display label for a customer: first/last name → a full-name attribute → email → phone →
 * "{Person} with id {id}".
 */
function customerDisplay(customer: ActivityLogCustomer, personSingular: string): string {
  return customerName(customer.attributes ?? {}) ?? customerIdentifierDisplay(customer, personSingular);
}

/** True when a webhook/slack action has no person attached (a data-campaign log → "N/A"). */
function isDataCampaignLog(log: ActivityLogRow['log']): boolean {
  if (log.type === 'webhook_event') return true;
  return isEmptyString(log.customer_id ?? '') && ['slack', 'webhook'].includes(log.delivery_type ?? '');
}

function isInviteLog(log: ActivityLogRow['log']): boolean {
  if (!isRecord(log.attrs)) return false;
  return isEmptyString(log.customer_id ?? '') && Boolean(log.attrs.recipient);
}

/**
 * Person/Object cell state ladder:
 * deleted profile → linked customer → invite → N/A (data-campaign / segment build) → Unknown.
 */
type PersonCellProps = {
  row: ActivityLogRow;
  workspaceId: string;
  personSingular: string;
};

export function PersonCell({ row, workspaceId, personSingular }: PersonCellProps) {
  const { log, customer } = row;

  if (!isEmptyString(log.old_customer_id ?? '')) {
    const label =
      log.type === 'profile_delete' ? log.old_customer_id : `Deleted ${personSingular} (${log.old_customer_id})`;
    // old_customer_id identifies the deleted profile — keep it out of FullStory.
    return (
      <Text size="s" className="fs-mask">
        {label}
      </Text>
    );
  }

  if (customer != null) {
    return (
      <Link
        href={generatePath('/workspaces/:workspaceId/journeys/people/:personId', {
          workspaceId,
          personId: customer.id,
        })}
        className="fs-mask"
      >
        {customerDisplay(customer, personSingular)}
      </Link>
    );
  }

  if (isInviteLog(log)) return <Text size="s">Invite recipient</Text>;
  if (isDataCampaignLog(log) || parseLogType(log.type) === 'segment_build') return <Text size="s">N/A</Text>;
  if (isEmptyString(log.customer_id ?? '')) return <Text size="s">Unknown</Text>;
  return <Text size="s">—</Text>;
}

/**
 * Object cell — the Person/Object column for object logs:
 * deleted object → linked "{type} {object name}" → Unknown. The object-type name/slug is
 * resolved from the workspace object-types map.
 *
 * NOTE: `WorkspaceObjectType` exposes `name` (no singular field), so the type prefix may read
 * plural; acceptable until a singular field is available.
 */
type ObjectCellProps = {
  row: ActivityLogRow;
  workspaceId: string;
  objectTypeById: Map<number, WorkspaceObjectType>;
};

export function ObjectCell({ row, workspaceId, objectTypeById }: ObjectCellProps) {
  const { log, object } = row;

  if (!isEmptyString(log.old_object_id ?? '')) {
    const isObjectDelete = parseLogType(log.type) === '_o:*:delete';
    // old_object_id identifies the deleted object — keep it out of FullStory (matches the object-name masking below).
    return (
      <Text size="s" className="fs-mask">
        {isObjectDelete ? log.old_object_id : `Deleted object (${log.old_object_id})`}
      </Text>
    );
  }

  if (object != null) {
    const objectType = objectTypeById.get(object.object_type_id);
    const attributes = object.attributes ?? {};
    const objectName = attributes.name ?? attributes.object_id ?? attributes.cio_object_id ?? '';
    if (objectType != null) {
      return (
        <Link
          href={generatePath('/workspaces/:workspaceId/journeys/objects/:objectSlug/:objectId', {
            workspaceId,
            objectSlug: objectType.slug,
            objectId: object.id,
          })}
          className="fs-mask"
        >
          {objectType.name} {objectName}
        </Link>
      );
    }
    return (
      <Text size="s" className="fs-mask">
        {objectName}
      </Text>
    );
  }

  return <Text size="s">Unknown</Text>;
}

/** A non-anon-merge log whose sources carry an anonymous_id (i.e. an anonymous-merge event). */
function isAnonymousMergeEvent(log: ActivityLog): boolean {
  if (log.type === 'anon_merge') return false;
  return (log.sources ?? []).some((s) => !isEmptyString(s.anonymous_id ?? ''));
}

/**
 * Activity Type cell — the human label, prefixed (for anonymous-merge events) with a person
 * icon + tooltip explaining the merge and linking to the anonymous-event settings.
 */
type TypeCellProps = {
  row: ActivityLogRow;
  label: string;
  workspaceId: string;
  personSingular: string;
};

export function TypeCell({ row, label, workspaceId, personSingular }: TypeCellProps) {
  const { log, customer } = row;

  if (!isAnonymousMergeEvent(log)) {
    return (
      <Text size="s" truncate>
        {label}
      </Text>
    );
  }

  // The merged-to label uses the person term so it reads consistently with the Person column and
  // the display name. For a known customer it resolves to a name/email/phone, so mask it from
  // FullStory session replay (same as the Person cell); the "this {term}" fallback carries no PII.
  const mergedTo =
    customer != null ? (
      <span className="fs-mask">{customerDisplay(customer, personSingular)}</span>
    ) : (
      `this ${personSingular}`
    );
  return (
    <Flex alignItems="center" gap="050">
      <Tooltip>
        <Icon name="person" size="sm" color="subtle" />
        <TooltipContent>
          This anonymous event was merged to {mergedTo}.{' '}
          {/* oxlint-disable-next-line local-rules/enforce-generate-path -- Hydra-owned settings route, not in the typed route table */}
          <Link href={`/workspaces/${workspaceId}/settings/anonymous_events`} variant="secondary">
            View anonymous event merge settings.
          </Link>
        </TooltipContent>
      </Tooltip>
      <Text size="s" truncate>
        {label}
      </Text>
    </Flex>
  );
}

function webhookCampaign(log: ActivityLog): { id: number; name: string } | null {
  const source = (log.sources ?? []).find((s) => s.campaign_id != null && !isEmptyString(s.campaign_name ?? ''));
  if (source?.campaign_id == null) return null;
  return { id: source.campaign_id, name: source.campaign_name ?? '' };
}

function inAppContent(log: ActivityLog): { id: number; name: string } | null {
  const source = (log.sources ?? []).find((s) => s.content_type === 'in_app_content');
  if (source?.content_id == null || isEmptyString(source.content_name ?? '')) return null;
  return { id: source.content_id, name: source.content_name ?? '' };
}

function segmentMembershipSummary(log: ActivityLog): { enteredCount: number; exitedCount: number } {
  if (!isRecord(log.attrs)) {
    return { enteredCount: 0, exitedCount: 0 };
  }
  const record = log.attrs;
  const entered = Array.isArray(record.entered) ? record.entered : [];
  const exited = Array.isArray(record.exited) ? record.exited : [];
  return { enteredCount: entered.length, exitedCount: exited.length };
}

/**
 * Activity Name cell:
 *  - draft / delivery logs link the delivery's subject (lazily fetched per row);
 *  - webhook-event campaign links, in-app content links, and segment membership/build links
 *    (from the payload / sideloaded segment);
 *  - everything else falls back to `displayName`.
 */
type NameCellProps = {
  row: ActivityLogRow;
  workspaceId: string;
  identifiers?: IdentifierConfig;
};

type DeliveryNameProps = {
  delivery: ReturnType<typeof useActivityLogDelivery>;
  log: ActivityLog;
  workspaceId: string;
};

/** Draft log: loading → linked draft subject → "Draft deleted" (terminal, never falls through). */
function DraftName({ delivery, log, workspaceId }: DeliveryNameProps) {
  if (delivery.isPending) {
    return (
      <Text size="s" color="subtle">
        Loading…
      </Text>
    );
  }
  if (!isEmptyString(delivery.subject ?? '')) {
    return (
      <Link
        href={generatePath('/workspaces/:workspaceId/journeys/outbox/drafts/:deliveryId', {
          workspaceId,
          deliveryId: log.delivery_id ?? '',
        })}
      >
        {delivery.subject}
      </Link>
    );
  }
  return (
    <Text size="s" color="subtle">
      Draft deleted
    </Text>
  );
}

/** Delivery log with a subject (or still loading); the record-gone case falls through to `TypeName`. */
function DeliveryName({ delivery, log, workspaceId }: DeliveryNameProps) {
  if (delivery.isPending) {
    return (
      <Text size="s" color="subtle">
        Loading…
      </Text>
    );
  }
  return (
    <Link
      href={generatePath('/workspaces/:workspaceId/journeys/outbox/deliveries/:deliveryId', {
        workspaceId,
        deliveryId: log.delivery_id ?? '',
      })}
    >
      {delivery.subject}
    </Link>
  );
}

type SegmentMembershipNameProps = {
  log: ActivityLog;
  segment: ActivityLogRow['segment'];
  workspaceId: string;
};

/** Segment-membership change: a linked segment when sideloaded, else an "entered/exited N" summary. */
function SegmentMembershipName({ log, segment, workspaceId }: SegmentMembershipNameProps) {
  const { enteredCount, exitedCount } = segmentMembershipSummary(log);
  if (segment != null) {
    return (
      <Text size="s">
        {enteredCount > 0 ? 'Entered ' : 'Exited '}
        <Link href={segmentPath(workspaceId, segment.id)}>{segment.name}</Link>
      </Text>
    );
  }
  const parts = [
    enteredCount > 0 ? `entered ${enteredCount}` : null,
    exitedCount > 0 ? `exited ${exitedCount}` : null,
  ].filter((part): part is string => part != null);
  const summary = parts.length > 0 ? parts.join(' and ') : 'changed';
  const label = `${summary} segment(s)`;
  return <Text size="s">{label.charAt(0).toUpperCase() + label.slice(1)}</Text>;
}

/** Type-specific name (no delivery subject): webhook/content/segment links, else the display name. */
function TypeName({ row, workspaceId, identifiers = {} }: NameCellProps) {
  const { log, segment } = row;

  if (log.type === 'webhook_event') {
    const campaign = webhookCampaign(log);
    if (campaign != null) {
      return (
        <Link
          href={generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', {
            workspaceId,
            campaignId: campaign.id,
          })}
        >
          {campaign.name}
        </Link>
      );
    }
  }

  if (CONTENT_EVENT_TYPES.includes(log.type)) {
    const content = inAppContent(log);
    if (content != null) {
      return (
        // oxlint-disable-next-line local-rules/enforce-generate-path -- in-app broadcasts route isn't in the typed route table
        <Link href={`/workspaces/${workspaceId}/journeys/in-app-broadcasts/${content.id}`}>{content.name}</Link>
      );
    }
  }

  if (log.type === 'profile_segment_membership_change') {
    return <SegmentMembershipName log={log} segment={segment} workspaceId={workspaceId} />;
  }

  if (log.type === 'segment_build') {
    return segment != null ? (
      <Link href={segmentPath(workspaceId, segment.id)}>{segment.name}</Link>
    ) : (
      <Text size="s">Segment deleted</Text>
    );
  }

  return (
    <Text size="s" truncate>
      {activityLogDisplayName(log, identifiers)}
    </Text>
  );
}

export function NameCell({ row, workspaceId, identifiers = {} }: NameCellProps) {
  const { log } = row;
  const hasDraft = log.type.includes('drafted_');
  const hasDelivery = !isEmptyString(log.delivery_id ?? '');

  // Lazy per-row delivery fetch (only when this log references a delivery), for the subject link.
  const delivery = useActivityLogDelivery(workspaceId, hasDraft || hasDelivery ? log.delivery_id : null);

  if (hasDraft) {
    return <DraftName delivery={delivery} log={log} workspaceId={workspaceId} />;
  }
  // Show the delivery link while loading or when it has a subject; a gone delivery record falls
  // through to the type-specific branches.
  if (hasDelivery && (delivery.isPending || !isEmptyString(delivery.subject ?? ''))) {
    return <DeliveryName delivery={delivery} log={log} workspaceId={workspaceId} />;
  }

  return <TypeName row={row} workspaceId={workspaceId} identifiers={identifiers} />;
}
